import { useEffect, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useStoreRegions, useStoreRegionsByLocation } from '../../hooks';
import { matchRegionByAddress } from '../../lib';
import type {
  AddressSuggestion,
  CreateStoreLocationRequest,
  StoreBusinessHours,
  StoreDayOfWeek,
  StoreLocationContact,
  StoreLocationResponse,
  StoreLocationServiceType,
  UpdateStoreLocationRequest,
} from '../../model/settingsTypes';
import { STORE_DAY_OF_WEEK_OPTIONS, STORE_SERVICE_LOCATION_TYPE_OPTIONS } from '../options';

import { AddressAutocompleteField } from './AddressAutocompleteField';

const LOCATION_EMPTY: CreateStoreLocationRequest = {
  code: '',
  name: '',
  type: 'STORE',
  addressId: null,
  address: '',
  city: '',
  latitude: null,
  longitude: null,
  phone: '',
  email: '',
  active: true,
  businessHours: [],
  contacts: [],
};

function cleanString(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

function parseNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** `Number(raw)`, guarding `NaN` — returns `fallback` (preserves whatever the field already
 * held) rather than clobbering a previously-entered coordinate when a picked suggestion
 * doesn't carry a parseable one. */
function parseCoordinate(raw: string | undefined, fallback: number | null | undefined): number | null {
  const parsed = raw !== undefined ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : (fallback ?? null);
}

/**
 * Normalizes a location's `businessHours` to a fixed 7-entry array, Monday→Sunday,
 * so the editor always has a row per day and every row's required fields
 * (`dayOfWeek`/`openTime`/`closeTime`) stay populated. Days missing from the source
 * default to closed (`active: false`) with a sensible 09:00–21:00 fallback window.
 *
 * Source entries are matched by `dayOfWeek`, not array position, so a partial or
 * out-of-order array from the server is handled correctly. If the source ever
 * contains more than one entry for the same day (or an entry whose `dayOfWeek`
 * isn't one of the 7 canonical values), only the first matching entry per day is
 * kept and the rest are silently dropped on the next save. This editor itself can
 * never produce such data — it's only reachable via a non-UI write (direct API/DB
 * access or a migration artifact) — but it's worth knowing before debugging a
 * "my extra business-hours row disappeared" report.
 */
function normalizeBusinessHours(hours: StoreBusinessHours[] | undefined): StoreBusinessHours[] {
  return STORE_DAY_OF_WEEK_OPTIONS.map(({ value: dayOfWeek }) => {
    const existing = hours?.find((entry) => entry.dayOfWeek === dayOfWeek);
    return existing ?? { dayOfWeek, openTime: '09:00', closeTime: '21:00', active: false };
  });
}

/** Fixed 7-row (Monday→Sunday) business-hours editor for {@link LocationDialog}. */
function LocationBusinessHoursEditor({
  value,
  onChange,
  disabled,
}: {
  value: StoreBusinessHours[];
  onChange: (value: StoreBusinessHours[]) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  // Re-normalize defensively rather than trusting `value` is already a full 7-entry
  // array: this keeps the editor correct regardless of the parent's effect timing,
  // and means `setDay` always has all 7 days to map over.
  const rows = useMemo(() => normalizeBusinessHours(value), [value]);

  const setDay = (day: StoreDayOfWeek, patch: Partial<StoreBusinessHours>) => {
    onChange(rows.map((entry) => (entry.dayOfWeek === day ? { ...entry, ...patch } : entry)));
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {t('storeProfileSettings.businessHoursEditor.title')}
      </Typography>
      <Stack spacing={0.5}>
        {STORE_DAY_OF_WEEK_OPTIONS.map((option) => {
          const hours = rows.find((entry) => entry.dayOfWeek === option.value);
          const dayActive = hours?.active ?? false;
          const dayLabel = t(option.label);
          return (
            <Box
              key={option.value}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '120px auto 1fr 1fr' },
                gap: { xs: 1, sm: 2 },
                alignItems: 'center',
                py: 0.75,
              }}
            >
              <Typography variant="body2">{dayLabel}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={dayActive}
                    disabled={disabled}
                    onChange={(event) => setDay(option.value, { active: event.target.checked })}
                    slotProps={{
                      input: {
                        'aria-label': t('storeProfileSettings.businessHoursEditor.dayOpenAria', {
                          day: dayLabel,
                        }),
                      },
                    }}
                  />
                }
                label={t('storeProfileSettings.businessHoursEditor.openLabel')}
              />
              <TextField
                label={t('storeProfileSettings.businessHoursEditor.opensLabel')}
                type="time"
                size="small"
                value={hours?.openTime ?? '09:00'}
                disabled={disabled || !dayActive}
                onChange={(event) => setDay(option.value, { openTime: event.target.value })}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    'aria-label': t('storeProfileSettings.businessHoursEditor.dayOpeningTimeAria', {
                      day: dayLabel,
                    }),
                  },
                }}
              />
              <TextField
                label={t('storeProfileSettings.businessHoursEditor.closesLabel')}
                type="time"
                size="small"
                value={hours?.closeTime ?? '21:00'}
                disabled={disabled || !dayActive}
                onChange={(event) => setDay(option.value, { closeTime: event.target.value })}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    'aria-label': t('storeProfileSettings.businessHoursEditor.dayClosingTimeAria', {
                      day: dayLabel,
                    }),
                  },
                }}
              />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

/** Dynamic add/remove editor for a location's additional {@link StoreLocationContact} entries. */
function LocationContactsEditor({
  value,
  onChange,
  disabled,
}: {
  value: StoreLocationContact[];
  onChange: (value: StoreLocationContact[]) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  const updateContact = (index: number, patch: Partial<StoreLocationContact>) => {
    onChange(value.map((contact, current) => (current === index ? { ...contact, ...patch } : contact)));
  };

  const removeContact = (index: number) => {
    onChange(value.filter((_, current) => current !== index));
  };

  const addContact = () => {
    onChange([...value, { phone: '', email: '', website: '' }]);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {t('storeProfileSettings.contactsEditor.title')}
      </Typography>
      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('storeProfileSettings.contactsEditor.empty')}
        </Typography>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1.5 }}>
          <Stack divider={<Divider />}>
            {value.map((contact, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="flex-start" sx={{ p: 2 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('storeProfileSettings.contactsEditor.contactTitle', { index: index + 1 })}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
                    <TextField
                      label={t('storeProfileSettings.contactsEditor.phoneLabel')}
                      size="small"
                      value={contact.phone ?? ''}
                      disabled={disabled}
                      onChange={(event) => updateContact(index, { phone: event.target.value })}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t('storeProfileSettings.contactsEditor.phoneAria', { index: index + 1 }),
                        },
                      }}
                    />
                    <TextField
                      label={t('storeProfileSettings.contactsEditor.emailLabel')}
                      type="email"
                      size="small"
                      value={contact.email ?? ''}
                      disabled={disabled}
                      onChange={(event) => updateContact(index, { email: event.target.value })}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t('storeProfileSettings.contactsEditor.emailAria', { index: index + 1 }),
                        },
                      }}
                    />
                    <TextField
                      label={t('storeProfileSettings.contactsEditor.websiteLabel')}
                      type="url"
                      size="small"
                      value={contact.website ?? ''}
                      disabled={disabled}
                      onChange={(event) => updateContact(index, { website: event.target.value })}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t('storeProfileSettings.contactsEditor.websiteAria', { index: index + 1 }),
                        },
                      }}
                    />
                  </Box>
                </Box>
                <IconButton
                  aria-label={t('storeProfileSettings.contactsEditor.removeAria', { index: index + 1 })}
                  color="error"
                  size="small"
                  disabled={disabled}
                  onClick={() => removeContact(index)}
                >
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
      <Button startIcon={<AddRoundedIcon />} onClick={addContact} disabled={disabled} size="small">
        {t('storeProfileSettings.contactsEditor.addButton')}
      </Button>
    </Box>
  );
}

/**
 * Transient hint shown under the Region field, derived from the last address-suggestion pick:
 * `matched` — a region was auto-detected (only rendered while the field still reflects that
 * auto-detection, i.e. the merchant hasn't manually overridden it since); `unmatched` — the
 * last pick's address didn't resolve to any known region.
 */
type RegionAutoHint = { kind: 'matched'; regionName: string } | { kind: 'unmatched' };

export interface LocationDialogProps {
  open: boolean;
  value: StoreLocationResponse | null;
  onClose: () => void;
  /**
   * `regionId` is the region currently selected in the dialog (`null` = no region), passed
   * alongside the location body so the caller (`LocationsTab`) can reconcile it against the
   * point's previously-bound region(s) after the location itself saves successfully.
   */
  onSubmit: (body: CreateStoreLocationRequest | UpdateStoreLocationRequest, regionId: string | null) => void;
  disabled: boolean;
}

/**
 * Create/edit dialog for a single store location (`web-store-service`). Also owns the
 * point↔region binding UI (folded in from the removed "Assignments" tab — the
 * `tabs.assignments` i18n key, now unrendered — see AC7): a single Region select whose value
 * is reconciled against the backend's many-to-many binding by the caller on save (see
 * `LocationDialogProps.onSubmit`'s doc comment and `LocationsTab.save` — this is a deliberate
 * one-region-per-point UI simplification over a many-to-many backend relationship, documented
 * in detail further below next to the seeding effect).
 */
export function LocationDialog({ open, value, onClose, onSubmit, disabled }: LocationDialogProps) {
  const { t } = useTranslation('settings');
  const [draft, setDraft] = useState<CreateStoreLocationRequest>(LOCATION_EMPTY);

  const [regionId, setRegionId] = useState('');
  // True the moment the merchant changes the Region field themselves. Once set, a later
  // address-suggestion pick must never silently override their manual choice again this
  // session — see `handleSelectSuggestion` below.
  const [regionTouchedByUser, setRegionTouchedByUser] = useState(false);
  const [regionAutoHint, setRegionAutoHint] = useState<RegionAutoHint | null>(null);
  // Guards the region-seeding effect below so it only ever runs once per dialog-open
  // session, even though `boundRegions.data` may only resolve asynchronously after the
  // dialog has already opened (see that effect's comment).
  const [regionSeeded, setRegionSeeded] = useState(false);

  const regions = useStoreRegions({ page: 0, size: 100 });
  const boundRegions = useStoreRegionsByLocation(value?.id ?? null);

  useEffect(() => {
    if (open) {
      setDraft(
        value
          ? {
              code: value.code,
              name: value.name,
              type: value.type,
              addressId: value.addressId ?? null,
              address: value.address ?? '',
              city: value.city ?? '',
              latitude: value.latitude ?? null,
              longitude: value.longitude ?? null,
              phone: value.phone ?? '',
              email: value.email ?? '',
              active: value.active,
              businessHours: normalizeBusinessHours(value.businessHours),
              contacts: value.contacts ?? [],
            }
          : { ...LOCATION_EMPTY, businessHours: normalizeBusinessHours(undefined) },
      );
      setRegionId('');
      setRegionTouchedByUser(false);
      setRegionAutoHint(null);
      setRegionSeeded(false);
    }
  }, [open, value]);

  /**
   * Seeds `regionId` from the point's current binding(s) in edit mode, taking the first bound
   * region if any. Runs once per open-session (guarded by `regionSeeded`) as soon as
   * `boundRegions` resolves — not folded into the reset effect above because that effect fires
   * synchronously on `open`/`value` while `boundRegions.data` is still loading the first time.
   *
   * Deliberate simplification: `web-store-service`'s point↔region binding is many-to-many, but
   * this single-select UI treats it as one-region-per-point (the removed "Assignments" tab
   * this dialog replaces is expected to behave the same way going forward). On save, `LocationsTab`
   * reconciles by unbinding any *other* previously-bound regions beyond the one selected here —
   * so editing a location that (via a non-UI write) ended up bound to more than one region will
   * collapse it down to just the one chosen in this dialog.
   *
   * `!regionTouchedByUser && regionId === ''` guards a race: `boundRegions` resolves
   * asynchronously (a network round trip) while the Region field is already interactive, so a
   * merchant can manually pick a region — or get an address-driven auto-match — before this
   * fetch lands. Without this guard, the seed would fire the moment `boundRegions.data` arrives
   * regardless of what happened in between, silently clobbering that choice with the point's
   * old server-side binding. `regionId === ''` (not just the touched flag) also covers the
   * auto-match case, since a matched suggestion sets `regionId` without setting
   * `regionTouchedByUser`.
   */
  useEffect(() => {
    if (
      open &&
      value &&
      !regionSeeded &&
      !regionTouchedByUser &&
      regionId === '' &&
      boundRegions.data
    ) {
      const first = boundRegions.data.items[0];
      if (first) setRegionId(first.id);
      setRegionSeeded(true);
    }
  }, [open, value, boundRegions.data, regionSeeded, regionTouchedByUser, regionId]);

  const set = <K extends keyof CreateStoreLocationRequest>(key: K, next: CreateStoreLocationRequest[K]) =>
    setDraft((current) => ({ ...current, [key]: next }));

  const handleSelectSuggestion = (suggestion: AddressSuggestion) => {
    setDraft((current) => ({
      ...current,
      address: suggestion.value,
      city: suggestion.city || suggestion.settlement || '',
      latitude: parseCoordinate(suggestion.geoLat, current.latitude),
      longitude: parseCoordinate(suggestion.geoLon, current.longitude),
      addressId: suggestion.fiasId ?? null,
    }));

    const matched = matchRegionByAddress(suggestion, regions.data?.items ?? []);
    if (matched) {
      setRegionAutoHint({ kind: 'matched', regionName: matched.name });
      if (!regionTouchedByUser) {
        setRegionId(matched.id);
      }
    } else {
      setRegionAutoHint({ kind: 'unmatched' });
    }
  };

  const handleRegionChange = (nextRegionId: string) => {
    setRegionId(nextRegionId);
    setRegionTouchedByUser(true);
  };

  const regionHelperText =
    regionAutoHint?.kind === 'matched' && !regionTouchedByUser
      ? t('storeProfileSettings.locationDialog.regionAutoDetectedHint', { region: regionAutoHint.regionName })
      : regionAutoHint?.kind === 'unmatched' && !regionId
        ? t('storeProfileSettings.locationDialog.regionNotDetectedHint')
        : undefined;

  const submit = () => {
    // Every business-hours row already carries valid required fields (see
    // `normalizeBusinessHours`), so the full 7-entry array passes through as-is.
    // Contacts are trimmed and rows left entirely blank (added, then abandoned) are
    // dropped so they aren't persisted as empty `{}` entries.
    const cleanedContacts = (draft.contacts ?? [])
      .map((contact) => ({
        phone: cleanString(contact.phone),
        email: cleanString(contact.email),
        website: cleanString(contact.website),
      }))
      .filter((contact) => contact.phone || contact.email || contact.website);

    const common = {
      name: draft.name.trim(),
      type: draft.type,
      addressId: cleanString(draft.addressId),
      address: cleanString(draft.address),
      city: cleanString(draft.city),
      latitude: draft.latitude ?? null,
      longitude: draft.longitude ?? null,
      phone: cleanString(draft.phone),
      email: cleanString(draft.email),
      businessHours: draft.businessHours ?? [],
      contacts: cleanedContacts,
    };
    const finalRegionId = regionId || null;
    if (value) {
      onSubmit(common, finalRegionId);
    } else {
      onSubmit({ ...common, code: draft.code.trim(), active: draft.active ?? true }, finalRegionId);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {value ? t('storeProfileSettings.locationDialog.editTitle') : t('storeProfileSettings.locationDialog.createTitle')}
      </DialogTitle>
      <DialogContent>
        {/* "Basic" section (sections.basic) — kept as a lighter Typography subtitle2 header
            (matching LocationBusinessHoursEditor/LocationContactsEditor's own existing
            section-heading style below) rather than a bordered SettingsCard: nesting a bordered
            card inside an already-bordered Dialog surface reads as heavy/double-framed in this
            design system's flat, border-led language — see design-web-app's elevation guidance. */}
        <Box sx={{ pt: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            {t('storeProfileSettings.locationDialog.sections.basic')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label={t('storeProfileSettings.locationDialog.codeLabel')}
              value={draft.code}
              disabled={disabled || Boolean(value)}
              onChange={(event) => set('code', event.target.value)}
              required
              size="small"
            />
            <TextField
              label={t('storeProfileSettings.locationDialog.nameLabel')}
              value={draft.name}
              disabled={disabled}
              onChange={(event) => set('name', event.target.value)}
              required
              size="small"
            />
            <TextField
              select
              label={t('storeProfileSettings.locationDialog.typeLabel')}
              value={draft.type}
              disabled={disabled}
              onChange={(event) => set('type', event.target.value as StoreLocationServiceType)}
              size="small"
            >
              {STORE_SERVICE_LOCATION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('storeProfileSettings.locationDialog.phoneLabel')}
              value={draft.phone ?? ''}
              disabled={disabled}
              onChange={(event) => set('phone', event.target.value)}
              size="small"
            />
            <TextField
              label={t('storeProfileSettings.locationDialog.emailLabel')}
              value={draft.email ?? ''}
              disabled={disabled}
              onChange={(event) => set('email', event.target.value)}
              size="small"
              type="email"
            />
            {!value ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.active ?? true}
                    disabled={disabled}
                    onChange={(event) => set('active', event.target.checked)}
                  />
                }
                label={t('storeProfileSettings.locationDialog.activeLabel')}
              />
            ) : null}
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            {t('storeProfileSettings.locationDialog.sections.addressAndRegion')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <AddressAutocompleteField
                label={t('storeProfileSettings.locationDialog.addressLabel')}
                value={draft.address ?? ''}
                onChange={(text) => set('address', text)}
                onSelectSuggestion={handleSelectSuggestion}
                disabled={disabled}
                helperText={t('storeProfileSettings.locationDialog.addressManualEntryHelper')}
              />
            </Box>
            <TextField
              label={t('storeProfileSettings.locationDialog.cityLabel')}
              value={draft.city ?? ''}
              disabled={disabled}
              onChange={(event) => set('city', event.target.value)}
              size="small"
            />
            <TextField
              select
              label={t('storeProfileSettings.locationDialog.regionLabel')}
              value={regionId}
              disabled={disabled}
              onChange={(event) => handleRegionChange(event.target.value)}
              helperText={regionHelperText}
              size="small"
            >
              <MenuItem value="">
                <em>{t('storeProfileSettings.locationDialog.regionNoneOption')}</em>
              </MenuItem>
              {(regions.data?.items ?? []).map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('storeProfileSettings.locationDialog.latitudeLabel')}
              value={draft.latitude ?? ''}
              disabled={disabled}
              onChange={(event) => set('latitude', parseNumber(event.target.value))}
              size="small"
            />
            <TextField
              label={t('storeProfileSettings.locationDialog.longitudeLabel')}
              value={draft.longitude ?? ''}
              disabled={disabled}
              onChange={(event) => set('longitude', parseNumber(event.target.value))}
              size="small"
            />
          </Box>
        </Box>

        <LocationContactsEditor
          value={draft.contacts ?? []}
          onChange={(next) => set('contacts', next)}
          disabled={disabled}
        />
        <LocationBusinessHoursEditor
          value={draft.businessHours ?? []}
          onChange={(next) => set('businessHours', next)}
          disabled={disabled}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:action.cancel')}</Button>
        {/*
          `boundRegions.isLoading` (edit mode only — disabled/never-loading in create mode, see
          the hook's `enabled: Boolean(locationId)`) gates Save shut while the point's existing
          region binding is still in flight. `LocationsTab.save()` reconciles bindings from a
          second subscription to this exact same query key/cache entry (`currentRegions`); without
          this gate a fast Save click could run reconciliation against a still-`undefined` snapshot,
          compute an empty "previously bound" set, and only ASSIGN the newly-picked region without
          ever unbinding the true previous one — silently leaving the point double-bound server-side
          even though the UI only ever shows one. Blocking Save until the shared fetch resolves
          closes that window outright rather than patching around it after the fact.
        */}
        <Tooltip
          title={boundRegions.isLoading ? t('storeProfileSettings.locationDialog.regionBindingLoadingHint') : ''}
        >
          <span>
            <Button
              variant="contained"
              onClick={submit}
              disabled={disabled || !draft.code.trim() || !draft.name.trim() || boundRegions.isLoading}
            >
              {t('common:action.save')}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
